import {
  CategoryConfigurationDefinitionResponse,
  ConfigurationFieldType,
  ConfigurationSectionType,
  SkuType,
} from '@ydistri/api-sdk';
import React from 'react';
import ConfigurationField from '../../../../../components/global/ConfigurationField/ConfigurationField';
import useCategoryId from '../../../../../hooks/useCategoryId';
import { useCategoryConfiguration } from '../../../../../hooks/useCategoryConfiguration';
import { Row, Text } from '@ydistri/ds';
import { InfoIconTooltip } from '../../../../../components/icons/InfoIconTooltip';
import useIsConfigurationEditable from '../../../../../hooks/useIsConfigurationEditable';

interface ConfigurationStrictSetsProps {
  sectionType: ConfigurationSectionType;
  definitions: CategoryConfigurationDefinitionResponse[];
}

interface StrictSetsFieldDefinitions {
  defUseStrictSet: ConfigurationFieldType;
  defUseMinCostUnitPrice: ConfigurationFieldType;
  defMinCostUnitPrice: ConfigurationFieldType;
}

const fieldTypesBySection: Partial<Record<ConfigurationSectionType, StrictSetsFieldDefinitions>> = {
  [ConfigurationSectionType.PotentialSourceStandardSubsection]: {
    defUseStrictSet: ConfigurationFieldType.PsUseStrictSets,
    defUseMinCostUnitPrice: ConfigurationFieldType.PsUseMinCostUnitPriceForSets,
    defMinCostUnitPrice: ConfigurationFieldType.PsMinCostUnitPriceForSets,
  },
  [ConfigurationSectionType.PotentialSourceForcedSubsection]: {
    defUseStrictSet: ConfigurationFieldType.PsUseStrictSetsForced,
    defUseMinCostUnitPrice: ConfigurationFieldType.PsUseMinCostUnitPriceForSetsForced,
    defMinCostUnitPrice: ConfigurationFieldType.PsMinCostUnitPriceForSetsForced,
  },
};

const getDefinition = (
  definitions: CategoryConfigurationDefinitionResponse[],
  fieldType?: ConfigurationFieldType,
) => {
  if (fieldType) {
    return definitions.find(definition => definition.fieldType === fieldType);
  }
};

const ConfigurationStrictSets: React.FC<ConfigurationStrictSetsProps> = ({
  sectionType,
  definitions,
}) => {
  const isConfigurationEditable = useIsConfigurationEditable();
  const currentFieldTypes = fieldTypesBySection[sectionType];

  const defUseStrictSet = getDefinition(definitions, currentFieldTypes?.defUseStrictSet);
  const defUseMinCostUnitPrice = getDefinition(
    definitions,
    currentFieldTypes?.defUseMinCostUnitPrice,
  );
  const defMinCostUnitPrice = getDefinition(definitions, currentFieldTypes?.defMinCostUnitPrice);

  const selectedCategoryId = useCategoryId();
  const { categoryConfigurationData: configurations } =
    useCategoryConfiguration(selectedCategoryId);

  if (!currentFieldTypes) return null;
  if (!defUseStrictSet) return null;

  const allControlsDisabled = !isConfigurationEditable;

  const configurationUseStrictSets =
    // eslint-disable-next-line typescript/no-unnecessary-condition -- configurations may be undefined for manual calculations
    configurations?.[defUseStrictSet.fieldType]?.[SkuType.FastMover];
  const configurationUseMinCostUnitPrice = defUseMinCostUnitPrice
    ? // eslint-disable-next-line typescript/no-unnecessary-condition -- configurations may be undefined for manual calculations
      configurations?.[defUseMinCostUnitPrice.fieldType]?.[SkuType.FastMover]
    : null;

  const isUseStrictSetsEnabled = configurationUseStrictSets?.value === '1';
  const isUseMinCostUnitPriceEnabled = configurationUseMinCostUnitPrice?.value === '1';

  const renderUseMinCostUnitPrice = isUseStrictSetsEnabled;
  const isMinCostUnitPriceAvailable =
    !allControlsDisabled && isUseStrictSetsEnabled && isUseMinCostUnitPriceEnabled;

  return (
    <Row $gap="3rem" $flexWrap="nowrap">
      <ConfigurationField
        dataType={defUseStrictSet.dataType}
        fieldType={defUseStrictSet.fieldType}
        skuTypeColumn={null}
        noAdditionalSpace
        disabled={allControlsDisabled}
      />

      <Row $flexWrap="nowrap" $alignItems="center">
        {defUseMinCostUnitPrice && renderUseMinCostUnitPrice && (
          <>
            <ConfigurationField
              dataType={defUseMinCostUnitPrice.dataType}
              fieldType={defUseMinCostUnitPrice.fieldType}
              skuTypeColumn={null}
              noAdditionalSpace={true}
              disabled={allControlsDisabled}
            />
            <Row $gap="0.5rem" $flexWrap="nowrap">
              {defUseMinCostUnitPrice.description && (
                <InfoIconTooltip htmlContent={defUseMinCostUnitPrice.description} />
              )}
              <Text
                $type={!allControlsDisabled && isUseMinCostUnitPriceEnabled ? 'default' : 'light'}
              >
                Below
              </Text>
            </Row>
          </>
        )}
        {defMinCostUnitPrice && renderUseMinCostUnitPrice && (
          <>
            <ConfigurationField
              dataType={defMinCostUnitPrice.dataType}
              fieldType={defMinCostUnitPrice.fieldType}
              skuTypeColumn={null}
              disabled={!isMinCostUnitPriceAvailable}
              inputWidth="5rem"
            />

            {defMinCostUnitPrice.description && (
              <InfoIconTooltip htmlContent={defMinCostUnitPrice.description} />
            )}
          </>
        )}
      </Row>
    </Row>
  );
};

export default ConfigurationStrictSets;
